// composables/useScrolling.ts
import { ref, nextTick, onMounted, onUnmounted, watch, type Ref } from 'vue'



/**
 * Checks if an element is visible within a scroll container.
 */
function isElementVisibleInContainer(
  element: HTMLElement,
  scrollContainer: HTMLElement,
): boolean {
  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();

  // Check vertical overlap
  const isVerticallyVisible =
    elementRect.bottom > containerRect.top &&
    elementRect.top < containerRect.bottom;

  // Check horizontal overlap
  const isHorizontallyVisible =
    elementRect.right > containerRect.left &&
    elementRect.left < containerRect.right;

  return isVerticallyVisible && isHorizontallyVisible;
}

/**
 * Returns an list of currently visible child elements within a scroll container.
 * This function uses `getBoundingClientRect()` and performs a synchronous layout calculation.
 */
export function getCurrentVisibleElements(
  scrollContainer: HTMLElement,
  childSelector: string | null = null,
): HTMLElement[] {
  const visibleElements: HTMLElement[] = [];

  let children: HTMLCollection | NodeListOf<Element>;

  if (childSelector) {
    children = scrollContainer.querySelectorAll(childSelector);
  } else {
    children = scrollContainer.children;
  }

  for (const child of Array.from(children)) {
    if (child instanceof HTMLElement && isElementVisibleInContainer(child, scrollContainer)) {
      visibleElements.push(child);
    }
  }

  return visibleElements;
}


interface ScrollOptions {
  behavior?: ScrollBehavior
  block?: ScrollLogicalPosition
  inline?: ScrollLogicalPosition
}

interface UseScrollingOptions {
  autoScroll?: boolean
  scrollThreshold?: number
  transitionDelay?: number
  smoothScroll?: boolean
  scrollOffset?: number

}

interface UseScrollingReturn {
  // Refs
  scrollContainer: Ref<HTMLElement | null>
  messageList: Ref<HTMLElement | null>
  isAtBottom: Ref<boolean>
  isScrolling: Ref<boolean>
  scrollPosition: Ref<number>
  scrollHeight: Ref<number>
  clientHeight: Ref<number>
  autoScrollEnabled: Ref<boolean>


  scrollToBottom: (options?: ScrollOptions) => void
  scrollToTop: (options?: ScrollOptions) => void
  scrollToPosition: (position: number, options?: ScrollOptions) => void
  scrollToElement: (element: HTMLElement, options?: ScrollOptions) => void
  enableAutoScroll: () => void
  disableAutoScroll: () => void
  toggleAutoScroll: () => void
  updateScrollInfo: () => void


  scrollPercentage: Ref<number>
  canScrollUp: Ref<boolean>
  canScrollDown: Ref<boolean>
}

export function useScrolling(options: UseScrollingOptions = {}): UseScrollingReturn {
  const {
    autoScroll = true,
    scrollThreshold = 100,
    transitionDelay = 100,
    smoothScroll = true,
    scrollOffset = 0,
  } = options

  const scrollContainer = ref<HTMLElement | null>(null)
  const messageList = ref<HTMLElement | null>(null)
  const isAtBottom = ref(true)
  const isScrolling = ref(false)
  const scrollPosition = ref(0)
  const scrollHeight = ref(0)
  const clientHeight = ref(0)
  const autoScrollEnabled = ref(autoScroll)
  const scrollTimeout = ref<number | null>(null)
  const resizeObserver = ref<ResizeObserver | null>(null)





  const scrollPercentage = ref(0)
  const canScrollUp = ref(false)
  const canScrollDown = ref(false)

  // Methods
  const updateScrollInfo = () => {
    if (!scrollContainer.value) return

    const container = scrollContainer.value
    scrollPosition.value = container.scrollTop
    scrollHeight.value = container.scrollHeight
    clientHeight.value = container.clientHeight

    // Calculate if we're at the bottom
    const threshold = scrollThreshold
    const bottomPosition = scrollHeight.value - clientHeight.value
    const currentPosition = scrollPosition.value

    isAtBottom.value = currentPosition >= bottomPosition - threshold

    // Calculate scroll percentage
    scrollPercentage.value = bottomPosition > 0
      ? Math.round((currentPosition / bottomPosition) * 100)
      : 100

    // Calculate scroll capabilities
    canScrollUp.value = currentPosition > 0
    canScrollDown.value = currentPosition < bottomPosition
  }

  const scrollToPosition = (
    position: number,
    scrollOptions: ScrollOptions = {}
  ) => {
    if (!scrollContainer.value) return

    const options: ScrollToOptions = {
      top: position + scrollOffset,
      behavior: smoothScroll ? 'smooth' : 'auto',
      ...scrollOptions,
    }

    scrollContainer.value.scrollTo(options)
  }

  const scrollToBottom = (scrollOptions: ScrollOptions = {}) => {

    if (!scrollContainer.value) return

    nextTick(() => {
      const container = scrollContainer.value!
      const maxScroll = container.scrollHeight - container.clientHeight
      scrollToPosition(maxScroll, scrollOptions)
    })
  }

  const scrollToTop = (scrollOptions: ScrollOptions = {}) => {
    scrollToPosition(0, scrollOptions)
  }

  const scrollToElement = (
    element: HTMLElement,
    scrollOptions: ScrollOptions = {},
    topMargin: number = 0
  ) => {
    if (!scrollContainer.value) return
    if (!element) return
    const containerRect = scrollContainer.value.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const relativeTop = elementRect.top - containerRect.top + scrollContainer.value.scrollTop
    // Adjust for top margin
    const adjustedTop = relativeTop - topMargin
    scrollToPosition(adjustedTop, scrollOptions)
  }

  const enableAutoScroll = () => {
    autoScrollEnabled.value = true
  }

  const disableAutoScroll = () => {
    autoScrollEnabled.value = false
  }

  const toggleAutoScroll = () => {
    autoScrollEnabled.value = !autoScrollEnabled.value
  }

  // Auto-scroll with transition delay
  const autoScrollToBottom = (delay = transitionDelay) => {
    if (!autoScrollEnabled.value) return

    setTimeout(() => {
      scrollToBottom()
    }, delay)
  }

  // Handle scroll events
  const handleScroll = () => {
    isScrolling.value = true
    updateScrollInfo()

    if (scrollTimeout.value) {
      clearTimeout(scrollTimeout.value)
    }

    scrollTimeout.value = window.setTimeout(() => {
      isScrolling.value = false
    }, 150)

    // Disable auto-scroll if user scrolls up manually
    if (!isAtBottom.value && autoScrollEnabled.value) {
      // Only disable if user scrolled up significantly
      const container = scrollContainer.value!
      const bottomPosition = container.scrollHeight - container.clientHeight
      const currentPosition = container.scrollTop

      if (currentPosition < bottomPosition - scrollThreshold * 2) {
        disableAutoScroll()
      }
    }
  }

  // Re-enable auto-scroll when user scrolls back to bottom
  watch(isAtBottom, (newValue) => {
    if (newValue && !autoScrollEnabled.value) {
      enableAutoScroll()
    }
  })

  // Setup resize observer to handle content changes
  const setupResizeObserver = () => {
    if (!messageList.value || !window.ResizeObserver) return
    let previousHeight = 0
    resizeObserver.value = new ResizeObserver((entries: ResizeObserverEntry[]) => {

      let heightDifference = 0
      for (const entry of entries) {
        // Get the new height
        const newHeight = entry.contentRect.height;
        // Calculate the height difference
        heightDifference = newHeight - previousHeight;
        // Update the previous height for the next observation
        previousHeight = newHeight;
      }
      updateScrollInfo()
      if (autoScrollEnabled.value && isAtBottom.value) {
        autoScrollToBottom(50)
      } else if (autoScrollEnabled.value && heightDifference > 600) {
        console.log('large content change detected, disabling autoscroll')
        // scroll to the top of the last message
        disableAutoScroll()
        updateScrollInfo()
        const nodes = getCurrentVisibleElements(scrollContainer.value!, '.moin_message-container')

        if (nodes.length > 0) {
          scrollToElement(nodes[nodes.length - 1], { behavior: 'smooth' }, 20)
        }
      } else {
        if (heightDifference < 600) {
          autoScrollToBottom(50)
          console.warn('try force autoscroll to bottom due to small height change, but might be canceled by autoscroll disabled')
        }
      }
      heightDifference = 0
      previousHeight = messageList.value?.scrollHeight || 0
    })

    resizeObserver.value.observe(messageList.value)
  }

  // Cleanup
  const cleanup = () => {
    if (scrollTimeout.value) {
      clearTimeout(scrollTimeout.value)
    }

    if (resizeObserver.value) {
      resizeObserver.value.disconnect()
    }

    if (scrollContainer.value) {
      scrollContainer.value.removeEventListener('scroll', handleScroll)
    }
  }

  // Lifecycle
  onMounted(() => {
    nextTick(() => {
      if (scrollContainer.value) {
        scrollContainer.value.addEventListener('scroll', handleScroll, { passive: true })
        updateScrollInfo()
        setupResizeObserver()

        if (autoScrollEnabled.value) {
          scrollToBottom({ behavior: 'auto' })
        }
      }
    })
  })

  onUnmounted(() => {
    cleanup()
  })

  // Watch for container changes
  watch(scrollContainer, (newContainer, oldContainer) => {
    if (oldContainer) {
      oldContainer.removeEventListener('scroll', handleScroll)
    }

    if (newContainer) {
      newContainer.addEventListener('scroll', handleScroll, { passive: true })
      updateScrollInfo()
      setupResizeObserver()
    }
  })

  return {
    // Refs
    scrollContainer,
    messageList,
    isAtBottom,
    isScrolling,
    scrollPosition,
    scrollHeight,
    clientHeight,
    autoScrollEnabled,

    // Methods
    scrollToBottom,
    scrollToTop,
    scrollToPosition,
    scrollToElement,
    enableAutoScroll,
    disableAutoScroll,
    toggleAutoScroll,
    updateScrollInfo,

    // Computed
    scrollPercentage,
    canScrollUp,
    canScrollDown,
  }
}
